import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * User-facing view for Basic Comment plugin - shows markdown content with read confirmation button.
 */
import { useState } from "react";
/**
 * Execution view for the basic comment step type.
 * Displays markdown content and allows user to confirm they have read it.
 * @param root0 - The component props
 * @param root0.config - The step configuration
 * @param root0.execution - Current execution state
 * @param root0.onStart - Callback to start the step
 * @param root0.onComplete - Callback to complete the step with data
 * @param root0.isLoading - Whether the step is currently loading
 * @param root0.isAccessible - Whether this step is accessible
 * @returns The execution view component
 */
export function ExecutionView({ config, execution, onStart, onComplete, isLoading, isAccessible, }) {
    const [note, setNote] = useState("");
    const isCompleted = execution?.state === "completed";
    const _isInProgress = execution?.state === "in_progress";
    const isPending = !execution || execution.state === "pending";
    /**
     * Starts the step if accessible
     */
    const handleStart = () => {
        if (!isAccessible)
            return;
        onStart();
    };
    /**
     * Completes the step with the optional confirmation note
     */
    const handleComplete = () => {
        onComplete({ confirmationNote: note || undefined });
    };
    // Show completed state
    if (isCompleted) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "rounded-lg bg-muted p-4", children: _jsx("div", { className: "prose prose-sm max-w-none dark:prose-invert", children: _jsx("div", { className: "whitespace-pre-wrap", children: config.content }) }) }), _jsxs("div", { className: "flex items-center gap-2 text-sm text-muted-foreground", children: [_jsx("svg", { className: "h-4 w-4 text-green-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M5 13l4 4L19 7" }) }), "Completed", execution.completedAt && (_jsxs("span", { children: ["on ", new Date(execution.completedAt).toLocaleDateString()] }))] })] }));
    }
    // Show pending state (not started)
    if (isPending) {
        return (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "rounded-lg border border-dashed p-4 text-center", children: _jsx("p", { className: "text-sm text-muted-foreground", children: "Click \"Start\" to view the content" }) }), _jsx("button", { onClick: handleStart, disabled: !isAccessible || isLoading, className: `
						rounded-md px-4 py-2 text-sm font-medium
						${isAccessible
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "cursor-not-allowed bg-muted text-muted-foreground"}
						disabled:opacity-50
					`, children: isLoading ? "Starting..." : "Start" }), !isAccessible && (_jsx("p", { className: "text-xs text-muted-foreground", children: "Complete previous steps to unlock" }))] }));
    }
    // Show in-progress state (content visible, waiting for confirmation)
    return (_jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "rounded-lg bg-muted p-4", children: _jsx("div", { className: "prose prose-sm max-w-none dark:prose-invert", children: _jsx("div", { className: "whitespace-pre-wrap", children: config.content }) }) }), _jsxs("div", { className: "space-y-2", children: [_jsx("label", { htmlFor: "confirmation-note", className: "text-sm font-medium", children: "Note (optional)" }), _jsx("textarea", { id: "confirmation-note", value: note, onChange: (e) => { setNote(e.target.value); }, placeholder: "Add any notes or comments...", rows: 2, className: "w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" })] }), _jsx("button", { onClick: handleComplete, disabled: isLoading, className: "rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50", children: isLoading ? "Completing..." : "I've read this" })] }));
}
//# sourceMappingURL=ExecutionView.js.map